/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './SelectReturnValueMapping';

const SelectReturnValueMappingManager: ComponentManagerFn<
  'SelectReturnValueMapping'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'SelectReturnValueMapping'>(
      platform,
      'SelectReturnValueMapping',
    ),
    type: 'SelectReturnValueMapping',
    name: 'Return Value Mapping',
    defaultValue,
    isPropertyVisible: (prop) => {
      switch (prop) {
        case 'sourceAttribute':
        case 'targetAttribute':
        case 'executeCondition':
          return true;
        default:
          return false;
      }
    },
    props: {
      executeCondition: {
        label: 'Execute Condition',
        type: 'expressionOnly',
        category: 'Data',
        returnType: () => 'boolean',
        helpText:
          'This action will be executed only if this condition is either blank or returns true.',
      },
      sourceAttribute: {
        aliasType: 'LOV',
        category: 'Data',
        getAttributeDataTypes: () => 'ALL',
        label: 'Source Attribute',
        required: true,
        type: 'ViewAttributeChooser',
      },
      targetAttribute: {
        aliasType: 'STORE',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        label: 'Target Attribute',
        preventMultiEdit: true,
        type: 'ViewAttributeChooser',
      },
    },
    getDisplayText: (item) => item.props.targetAttribute,
    category: 'Forms',
    icon: { iconName: 'arrows-h' },
    description: `**Overview**<br/>
The Select Return Value Mapping component is a specialized logic helper designed exclusively for the **Select** component. It functions identically to the **LOV Map** but is built specifically for dropdown menus. It enables "autofill" behavior: when a user selects an option from a dropdown, this component automatically copies additional data from that selection into other fields on the form.
Use this component when a dropdown selection (e.g., "Product") should automatically populate related fields (e.g., "Price" and "SKU") without requiring extra user input.

**1. Mapping Logic**<br/>
The component acts as a data bridge between the dropdown's option list and the form's fields.
* **Source Attribute:** The field in the dropdown's data source that contains the value to copy.
* **Alias Type:** **LOV** (Refers to the dropdown's source data).
* **Target Attribute:** The field in your current form where the value will be pasted.
* **Alias Type:** **STORE** (Refers to the form's data store).

**2. Conditional Execution**<br/>
You can control exactly when this autofill happens to avoid overwriting existing data unintentionally.
* **Execute Condition:** A boolean expression.
* **Function:** The mapping only runs if this expression evaluates to **true** (or is left blank).
* **Use Case:** You might want to overwrite the "Price" field only if the "Price" field is currently empty, protecting manual overrides.

**3. Best Use Cases**<br/>
* **Product Selection:** When picking a product from a dropdown, automatically fill the "Unit Price" and "Description" fields.
* **Address Autofill:** When selecting a "Shipping Address" from a saved list, fill in the "Street", "City", and "Zip" fields.
* **Default Values:** When selecting an "Employee Type" (e.g., Contractor), automatically set the "Contract End Date" field to a specific default value.`,
  };
};

export default SelectReturnValueMappingManager;
